/**
 * 外部ページへのリンクボタン
 * window.open()は端末のポップアップ抑止やアプリ内ブラウザで何も起きないことがあるため、
 * 利用者が押す本物のリンク（<a target="_blank">）をボタンの見た目で描きます。
 */

import React, { useEffect } from 'react';
import { Text } from './WebPrimitives';
import { logWarn } from '../utils/logger';

/** 外部リンクボタンのプロパティ */
type ExternalLinkButtonProps = {
  /** 開くURL。http / https 以外はリンクを描かない */
  href: string;
  /** ボタンの文言 */
  label: string;
  /** 押したときに何が起きるかの補足 */
  accessibilityHint?: string;
  /** 枠の見た目。既定は共通ボタン（AppButton）の主ボタン */
  className?: string;
  /** 文言の見た目 */
  labelClassName?: string;
  testID?: string;
};

/** 共通ボタン（AppButton）の主ボタンと同じ見た目 */
const DEFAULT_LINK_CLASS_NAME =
  'min-h-[48px] border-2 border-[#0F1A2E] bg-app-quest-night px-4 py-2';
/** 共通ボタン（AppButton）の主ボタンの文言と同じ見た目 */
const DEFAULT_LABEL_CLASS_NAME =
  'text-center text-sm font-bold tracking-wide text-app-primary-on';

/**
 * 新しいタブで開いてよい外部URLか判定する
 * @param {string} href - URL
 * @returns {boolean} http / https のURLの場合true
 */
const isOpenableExternalUrl = (href: string): boolean => {
  try {
    const { protocol } = new URL(href);
    return protocol === 'https:' || protocol === 'http:';
  } catch {
    return false;
  }
};

/**
 * 外部ページを新しいタブで開くリンクをボタンの見た目で描く
 * @param {ExternalLinkButtonProps} props - プロパティ
 * @returns {React.JSX.Element} リンク、または開けない旨の案内
 */
const ExternalLinkButton = ({
  href,
  label,
  accessibilityHint = '新しいタブで開きます',
  className = DEFAULT_LINK_CLASS_NAME,
  labelClassName = DEFAULT_LABEL_CLASS_NAME,
  testID,
}: ExternalLinkButtonProps): React.JSX.Element => {
  const isOpenable = isOpenableExternalUrl(href);

  useEffect(() => {
    if (!isOpenable) {
      logWarn('http / https 以外の外部リンクは開かないため、リンクを表示しませんでした。', { label });
    }
  }, [isOpenable, label]);

  if (!isOpenable) {
    return (
      <Text className="text-sm font-medium text-app-danger" testID={testID}>
        リンクを開けません。スタッフにお声がけください。
      </Text>
    );
  }

  return (
    <a
      aria-description={accessibilityHint}
      className={`flex flex-col items-center justify-center no-underline ${className}`}
      data-testid={testID}
      href={href}
      rel="noopener noreferrer"
      target="_blank"
    >
      <span className={labelClassName}>{label}</span>
    </a>
  );
};

export default ExternalLinkButton;
